/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-account
 * @responsibility Own the backend-authoritative SET-F02 profile query and its update/email-change commands.
 * @state-owner TanStack Query
 * @allowed-imports app environment/session; auth module public API; same-module API and contracts; TanStack Query
 * @forbidden component UI; fabricated profile data; direct Supabase calls
 * @public-api settings-account hooks
 * @side-effects authenticated profile read/write and cache invalidation
 * @tests frontend/packages/workspace-web/src/modules/settings-account/components/AccountSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VispoApiError } from '@vispo/api-client';
import type { ProfileContext, UpdateProfileInput } from '@vispo/contracts';
import { useOptionalWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { loadMeContext } from '../../auth';
import { updateProfile, uploadAvatar } from './settings-account';

export const settingsAccountKeys = {
  profile: (userId: string) => ['set-f02', 'profile', userId] as const,
};

const DEMO_PROFILE: ProfileContext = Object.freeze({
  display_name: 'Demo User',
  avatar_url: null,
  revision: 1,
});

export function useProfile() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  return useQuery({
    queryKey: settingsAccountKeys.profile(userId),
    enabled: Boolean(environment && (session || environment.demoMode)),
    // Demo data is a locally-mutated seed, not server state: an automatic background
    // refetch (window focus, reconnect) would silently discard an in-flight edit.
    staleTime: environment?.demoMode ? Number.POSITIVE_INFINITY : undefined,
    queryFn: async (): Promise<ProfileContext> => {
      if (environment?.demoMode) {
        const e2e = globalThis as typeof globalThis & {
          __VISPO_SET_F02_PROFILE_LOAD_DEFERRED__?: boolean;
          __VISPO_SET_F02_PROFILE_LOAD_RESOLVE__?: () => void;
        };
        if (e2e.__VISPO_SET_F02_PROFILE_LOAD_DEFERRED__) {
          await new Promise<void>((resolve) => {
            e2e.__VISPO_SET_F02_PROFILE_LOAD_RESOLVE__ = resolve;
          });
          delete e2e.__VISPO_SET_F02_PROFILE_LOAD_DEFERRED__;
          delete e2e.__VISPO_SET_F02_PROFILE_LOAD_RESOLVE__;
        }
        return {
          display_name: session?.displayName ?? DEMO_PROFILE.display_name,
          avatar_url: session?.avatarUrl ?? DEMO_PROFILE.avatar_url,
          revision: DEMO_PROFILE.revision,
        };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const context = await loadMeContext(environment.apiClient);
      if (!context.profile) throw new Error('帳號資料尚未建立');
      return context.profile;
    },
  });
}

export function useUpdateProfile() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  const queryClient = useQueryClient();
  const queryKey = settingsAccountKeys.profile(userId);

  return useMutation({
    mutationFn: async (
      input: Omit<UpdateProfileInput, 'idempotencyKey'>,
    ): Promise<ProfileContext> => {
      const command: UpdateProfileInput = {
        ...input,
        idempotencyKey: `agent-a-set-f02-${crypto.randomUUID()}`,
      };
      if (environment?.demoMode) {
        const current = queryClient.getQueryData<ProfileContext>(queryKey);
        if (!current) throw new Error('帳號資料尚未準備完成');
        const forcedConflict = (
          globalThis as typeof globalThis & {
            __VISPO_SET_F02_FORCE_REVISION_CONFLICT__?: boolean;
          }
        ).__VISPO_SET_F02_FORCE_REVISION_CONFLICT__;
        if (forcedConflict || current.revision !== command.expectedRevision) {
          throw new VispoApiError(
            {
              code: 'ACCOUNT_REVISION_CONFLICT',
              message: 'The profile changed; refresh and retry.',
              details: undefined,
              requestId: 'demo',
            },
            409,
          );
        }
        return {
          display_name: command.displayName ?? current.display_name,
          avatar_url: command.clearAvatar ? null : current.avatar_url,
          revision: current.revision + 1,
        };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await updateProfile(environment.apiClient, command);
      return response.profile;
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKey, profile);
    },
  });
}

export function useUploadAvatar() {
  const environment = useOptionalWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const userId = session?.userId ?? 'anonymous';
  const queryClient = useQueryClient();
  const queryKey = settingsAccountKeys.profile(userId);

  return useMutation({
    mutationFn: async (file: File): Promise<ProfileContext> => {
      if (environment?.demoMode) {
        const current = queryClient.getQueryData<ProfileContext>(queryKey);
        if (!current) throw new Error('帳號資料尚未準備完成');
        return {
          display_name: current.display_name,
          avatar_url: URL.createObjectURL(file),
          revision: current.revision + 1,
        };
      }
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      const response = await uploadAvatar(
        environment.apiClient,
        file,
        `agent-a-set-f11-${crypto.randomUUID()}`,
      );
      return response.profile;
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKey, profile);
    },
  });
}

export function useRequestEmailChange() {
  const environment = useOptionalWorkspaceEnvironment();
  return useMutation({
    mutationFn: async (newEmail: string) => {
      if (!environment) throw new Error('WorkspaceEnvironment is missing');
      await environment.auth.requestEmailChange(newEmail);
    },
  });
}
